// Types
import { IPageParam } from "@/Common/api/types/pagination";

// APIs
import { getFniDetails } from "@DataPages/api/financeManagement/fniDetails/queries";

// Hooks
import { usePaginatedResource } from "@/Common/hooks/Pagination";
import { buildGetNextPageParam } from "@/Common/hooks/Pagination/helpers";

// Helpers
import { and } from "@/Common/helpers/sonar";

// Constants
const PAGINATION_SIZE = 500;

export function getFniDetailsQueryKey(args: { locationId: string; commissionCycleId: string; payPeriodId: string }) {
	const { locationId, commissionCycleId, payPeriodId } = args;
	return ["fniDetails", locationId, commissionCycleId, payPeriodId];
}

export function useFniDetailsQuery(args: {
	enabled: boolean;
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	paginationSize?: number;
}) {
	const { enabled, locationId, commissionCycleId, payPeriodId, paginationSize = PAGINATION_SIZE } = args;

	const {
		data: fniDetailsData,
		isLoading,
		isFetching,
		hasNextPage,
		...otherResults
	} = usePaginatedResource({
		queryKey: getFniDetailsQueryKey({ locationId, commissionCycleId, payPeriodId }),
		enabled: and(enabled, !!locationId, !!commissionCycleId, !!payPeriodId),
		initialPageParam: { after: "", first: paginationSize },
		getNextPageParam: buildGetNextPageParam(paginationSize),
		fetchAllPages: true,
		staleTime: Infinity,
		gcTime: Infinity,
		queryFn: async ({ pageParam }) => {
			const typedPageParam = pageParam as IPageParam<string>;
			if (!typedPageParam || !("after" in typedPageParam) || !("first" in typedPageParam)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}

			const paginatedResponse = await getFniDetails({
				locationId,
				commissionCycleId,
				payPeriodId,
				first: typedPageParam.first,
				after: typedPageParam.after,
			});

			if (!paginatedResponse) {
				throw new Error("Error retrieving F&I Details data");
			}

			return paginatedResponse;
		},
	});

	return {
		fniDetailsData,
		isLoading,
		isPaginating: isFetching || hasNextPage,
		...otherResults,
	};
}
